<template>
  <div class="layout-info-holder">
    <div class="info-unit-group group-lines">
      <div class="segment-label-large">
        {{ getInletCount }}
      </div>
      <div class="info-line-short"></div>
    </div>
    <div class="info-line-long"></div>
    <div class="large-group">
      <div class="segment-label-large">
        {{ getPipesLabel }}
      </div>
      <div class="info-num-large">
        <span v-if="this.$props.side != 'left'">
          {{ this.filterLength(getPipesCount) }}
        </span>
        <span v-else>
          {{ getPipesCount }}
        </span>
        <span v-if="this.$props.side != 'left'" class="large-unit">
          {{ lengthUnit }}
        </span>
      </div>
    </div>
    <div v-if="isFlush" class="info-details">
      <div class="info-unit-group">
        <div class="info-line-short"></div>
        <div class="segment-label-large spacer">
          {{ getSide }} FLOW
        </div>
        <div class="info-num-holder">
          <div class="info-num">
            <span :id="side + '-flow'">
              {{ getFlow }}
            </span>
          </div>
          <div :id="side + '-flow-unit'" class="info-unit">
            {{ this.flowUnit }}
          </div>
        </div>
      </div>
      <div class="info-unit-group">
        <div class="info-line-short"></div>
        <div class="segment-label-large spacer">
          {{ getSide }} FLUSH
        </div>
        <div class="info-num-holder">
          <div class="info-icon" @click="handleIcon">
            <img :id="side + '-flush'" :data-clamped="getUnclamped" :src="getIcon" class="leg-icon"/>
          </div>
        </div>
      </div>
      <div class="info-unit-group">
        <div class="info-line-short"></div>
        <div class="segment-label-large spacer">
          {{ getSide }} PRESSURE
        </div>
        <div class="info-num-holder">
          <div class="info-num">
            <span :id="side + '-pressure'">
              {{ getPressure }}
            </span>
          </div>
          <div :id="side + '-pressure-unit'" class="info-unit">
            {{ this.pressureUnit }}
          </div>
        </div>
      </div>
      <div class="info-unit-group">
        <div class="info-line-short"></div>
        <div class="segment-label-large spacer">
          {{ getSide }} VELOCITY
        </div>
        <div class="info-num-holder">
          <div class="info-num">
            <span :id="side  + '-velocity'">
              {{ getVelocity }}
            </span>
          </div>
          <div :id="side + '-velocity-unit'" class="info-unit">
            {{ this.velocityUnit }}
          </div>
        </div>
      </div>
      <div class="info-unit-group">
        <div class="info-line-short"></div>
        <div class="segment-label-large spacer">
          {{ getSide }} FLUSH TIME
        </div>
        <div class="info-num-holder">
          <div class="info-num">
            <span :id="side + '-flushTime'">
              {{ getFlush }}
            </span>
          </div>
          <div :id="side + '-flushTime-unit'" class="info-unit">
            {{ this.timeUnit }}
          </div>
        </div>
      </div>
    </div>
    <div class="info-spacer"></div>
    <div class="large-group last-item">
      <div class="segment-label-large">
        {{ getSlopesLabel }}
      </div>
      <div class="info-num-large">
        {{ getSlopesCount }}
        <span v-if="this.$props.side != 'left'" class="large-unit">
          {{ this.lengthUnit }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { useSegmentStore } from '@/store/SegmentStore';
import * as UnitConversion from '@/services/UnitConversionService';
import { useSettingsStore } from '@/store/SettingsStore';
import { useInputStore } from '@/store/InputStore';
import { useSprinklerStore } from '@/store/SprinklerStore';

export default {
  data () {
    return {
      isLengthUnit: null,
    };
  },
  name: 'LayoutInfo',
  props: ['side'],
  watch: {
    getLengthUnit (to, from) {
      if (to !== from) {
        this.isLengthUnit = true;
      }
    },
  },
  computed: {
    isFlush () {
      return useSettingsStore().IsFlush;
    },
    getFlow () {
      return (this.side === 'left')
        ? this.filterFlow(this.getCalcLeftLeg.outletFlow).toFixed(3)
        : this.filterFlow(this.getCalcRightLeg.outletFlow).toFixed(3);
    },
    getUnclamped () {
      return useSegmentStore().getIsUnclamped(this.$props.side);
    },
    getFlush () {
      return (this.side === 'left')
        ? this.filterTime(this.getCalcLeftLeg.flushTime)
        : this.filterTime(this.getCalcRightLeg.flushTime);
    },
    flowUnit () {
      return useSettingsStore().getFlowUnit;
    },
    lengthUnit () {
      return useSettingsStore().getLengthUnit;
    },
    pressureUnit () {
      return useSettingsStore().getPressureUnit;
    },
    timeUnit () {
      return useSettingsStore().getTimeUnit;
    },
    velocityUnit () {
      return useSettingsStore().getVelocityUnit;
    },
    getCalcLeftLeg () {
      const legs = useInputStore().getCalcData.legs;
      if (legs.length === 1 && useSegmentStore().getInletPosition === 0) {
        return {
          outletFlow: 0,
          outletPressure: 0,
          outletVelocity: 0,
          flushTime: 0,
        };
      } else {
        return legs[0];
      }
    },
    getCalcRightLeg () {
      const legs = useInputStore().getCalcData.legs;
      if (legs.length === 1) {
        const segmentStore = useSegmentStore();
        if (segmentStore.getInletPosition === Math.round(segmentStore.getAllSegmentLengths('pipe'))) {
          return {
            outletFlow: 0,
            outletPressure: 0,
            outletVelocity: 0,
            flushTime: 0,
          };
        } else {
          return legs[0];
        }
      } else {
        return legs[1];
      }
    },
    getLengthUnit () {
      return this.isLengthUnit;
    },
    getPipesLabel () {
      return this.$props.side === 'left' ? 'pipes' : 'total pipe length';
    },
    getPipesCount () {
      const segmentStore = useSegmentStore();
      return this.$props.side === 'left' ? segmentStore.numberOfSegmentsOfType('pipe') : segmentStore.getAllSegmentLengths('pipe');
    },
    getSlopesLabel () {
      return this.$props.side === 'left' ? 'slopes' : 'total slope length';
    },
    getSlopesCount () {
      const segmentStore = useSegmentStore();
      return this.$props.side === 'left' ? segmentStore.numberOfSegmentsOfType('slope') : UnitConversion.FilterLength(segmentStore.getAllSegmentLengths('slope'), this.lengthUnit).toFixed(2);
    },
    getIcon () {
      return require('@/assets/svg/SVG/' + (this.getUnclamped ? 'flow' : 'clamped') + '.svg');
    },
    getSide () {
      return this.$props.side.toUpperCase();
    },
    getInletCount () {
      let count = useSegmentStore().getInletCount(this.$props.side);
      return count + ' ' + useSprinklerStore().getSelectedModel + ' ' +  this.getSide + ' OF INLET';
    },
    getPressure () {
      return this.filterPressure((this.$props.side === 'left' ? this.getCalcLeftLeg : this.getCalcRightLeg).outletPressure).toFixed(2);
    },
    getVelocity () {
      return this.filterVelocity((this.$props.side === 'left' ? this.getCalcLeftLeg : this.getCalcRightLeg).outletVelocity);
    }
  },
  methods: {
    handleIcon () {
      useSegmentStore().toggleIsClamped(this.$props.side);
      useInputStore().sprinklerCalc();
    },
    filterLength (length) {
      return UnitConversion.FilterLength(length, this.lengthUnit).toFixed(2);
    },
    filterVelocity (velocity) {
      return UnitConversion.FilterVelocity(velocity, this.velocityUnit).toFixed(3);
    },
    filterFlow (flow) {
      return UnitConversion.FilterFlow(flow, this.flowUnit);
    },
    filterTime (time) {
      // added this in to gix error og inffinity on the front ennd
      if (time == Infinity || isNaN(time)) {
        return 'N/A';
      } else {
        return UnitConversion.FilterTime(time, this.timeUnit).toFixed(3);
      }
    },
    filterPressure (pressure) {
      return UnitConversion.FilterPressure(pressure, useSettingsStore().getPressureUnit);
    },
  },
};
</script>

<style scoped>
@media (max-width: 1024px), (max-height: 1024px) {
  .info-num {
    font-size: 90% !important;
    padding-top: 5px !important;
  }
  .spacer {
    margin-bottom: 0 !important;
  }
  .info-unit {
    font-size: 70% !important;
    margin-top: -4px;
  }
  .info-num-large {
    font-size: 100% !important;
  }
  .segment-label-large {
    display: flex;
    flex-direction: row;
    font-size: 55% !important;
  }
}
.layout-info-holder {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  user-select: none;
  border-top: 0.5px solid #9f9f9f;
  border-left: 0.5px solid #9f9f9f;
  border-right: 0.5px solid #9f9f9f;
  border-bottom: 0.5px solid #9f9f9f;
}
.segment-label {
  font-size: 65%;
  font-weight: bold;
  text-transform: uppercase;
  height: 16px;
}
.info-num-large {
  font-size: 100%;
  font-weight: bold;
}
.info-num {
  font-size: 100%;
  font-weight: bold;
  margin-top: -4px;
}
.info-unit {
  font-size: 75%;
  font-weight: 600;
  color: gray;
  width: 100%;
  margin-top: -4px;
}
.info-line-short {
  background-color: #a4a4a4;
  width: 100%;
}
.info-icon {
  height: 100%;
  aspect-ratio: 1/1;
  max-height: 100px;
  margin-top: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.leg-icon {
  height: 35px;
}
@media (max-width: 1024px), (max-height: 1024px) {
  .leg-icon {
    height: 30px;
  }
  .info-icon {
    margin-top: 3px;
    margin-bottom: 3px;
  }
}
.large-group {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}
.last-item {
  margin-top: auto;
}
.info-details {
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-direction: column;
  min-height: 150px;
}
.info-details .info-unit-group {
  flex: 1;
}
.info-unit-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}
.info-spacer {
  height: 14px;
  border-top: 1px solid gray;
  border-bottom: 1px solid gray;
  width: 100%;
}
.segment-label-large {
  font-size: 100%;
  font-weight: bold;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  color: #1e1e1e;
}
.spacer {
  background-color: whitesmoke;
  width: 99%;
  border-top: 1px solid #adadad;
}
.large-unit {
  font-size: 60%;
}
.group-lines {
  border-bottom: 1px solid #adadad;
}
.info-eye-hide-icon {
  width: 14px;
}
.info-num-holder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}
</style>
